import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@repo/ui";
import { getHelpUrl } from "@repo/utils";
import {
  Question,
  ArrowSquareOut,
  Package,
  ArrowLineDown,
  ArrowLineUp,
  Pulse,
  Gear,
  Lifebuoy,
  BookOpen,
  CaretRight,
  Bug,
  Lightbulb,
  NavigationArrow,
} from "@phosphor-icons/react";
import { SupportTicketDialog } from "./support-ticket-dialog";

interface PopularLink {
  label: string;
  url: string;
  icon: React.ReactNode;
}

const POPULAR_LINKS: PopularLink[] = [
  {
    label: "Managing Inventory",
    url: "/docs/inventory/managing-stock",
    icon: <Package weight="fill" size={14} />,
  },
  {
    label: "Recording a Dispense",
    url: "/docs/dispenses",
    icon: <ArrowLineUp weight="bold" size={14} />,
  },
  {
    label: "Recording a Receipt",
    url: "/docs/receipts",
    icon: <ArrowLineDown weight="bold" size={14} />,
  },
  {
    label: "Reading Activity Logs",
    url: "/docs/activity-and-reports",
    icon: <Pulse weight="fill" size={14} />,
  },
  {
    label: "Users & Settings",
    url: "/docs/users-and-settings",
    icon: <Gear weight="fill" size={14} />,
  },
  {
    label: "Troubleshooting",
    url: "/docs/troubleshooting",
    icon: <Lifebuoy weight="fill" size={14} />,
  },
];

interface HelpPopoverProps {
  onStartTour?: () => void;
}

export function HelpPopover({ onStartTour }: HelpPopoverProps = {}) {
  const [open, setOpen] = React.useState(false);
  const [ticketType, setTicketType] = React.useState<
    "bug_report" | "feature_request" | null
  >(null);

  const [helpBaseUrl, setHelpBaseUrl] = React.useState(
    "https://help.c2vault.com"
  );

  React.useEffect(() => {
    setHelpBaseUrl(getHelpUrl(window.location.hostname));
  }, []);

  // Keyboard shortcut: Cmd+/
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  function handleLinkClick(url: string) {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            title="Help (⌘/)"
            aria-label="Help"
            className="relative flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-muted/60 hover:text-foreground"
          >
            <Question size={17} weight="bold" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-80 p-0 shadow-xl border border-border/60"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <BookOpen size={15} weight="fill" className="text-primary" />
              Help Center
            </div>
            <a
              href={helpBaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Browse all
              <ArrowSquareOut size={12} />
            </a>
          </div>

          {/* Take a Tour */}
          {onStartTour && (
            <div className="border-b border-border px-4 py-2.5">
              <button
                onClick={() => {
                  setOpen(false);
                  onStartTour();
                }}
                className="flex w-full items-center gap-2.5 rounded-md px-0 py-1.5 text-left text-sm text-foreground hover:text-primary transition-colors group"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <NavigationArrow size={14} weight="fill" />
                </span>
                <span className="flex-1 font-medium">Take a Tour</span>
                <CaretRight
                  size={12}
                  className="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </button>
            </div>
          )}

          {/* Popular articles */}
          <div className="py-1">
            <p className="px-4 pb-1 pt-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Popular articles
            </p>
            {POPULAR_LINKS.map((link) => (
              <button
                key={link.url}
                onClick={() => handleLinkClick(`${helpBaseUrl}${link.url}`)}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-foreground hover:bg-primary/5 transition-colors group"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  {link.icon}
                </span>
                <span className="flex-1">{link.label}</span>
                <CaretRight
                  size={12}
                  className="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </button>
            ))}
          </div>

          {/* Open Help Center CTA */}
          <div className="border-t border-border px-4 py-3">
            <a
              href={helpBaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
            >
              <BookOpen size={15} weight="fill" />
              Open Help Center
              <ArrowSquareOut size={13} />
            </a>
          </div>

          {/* Feedback Actions */}
          <div className="border-t border-border px-4 py-2 space-y-0.5">
            <button
              onClick={() => {
                setTicketType("bug_report");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-0 py-2 text-left text-sm text-foreground hover:text-primary transition-colors group"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <Bug size={14} weight="fill" />
              </span>
              <span className="flex-1">Report a Problem</span>
              <CaretRight
                size={12}
                className="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </button>
            <button
              onClick={() => {
                setTicketType("feature_request");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-0 py-2 text-left text-sm text-foreground hover:text-primary transition-colors group"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-500/10 text-amber-600">
                <Lightbulb size={14} weight="fill" />
              </span>
              <span className="flex-1">Suggest a Feature</span>
              <CaretRight
                size={12}
                className="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </button>
          </div>

          {/* Footer */}
          <div className="border-t border-border px-4 py-2.5">
            <button
              onClick={() =>
                handleLinkClick(
                  `${helpBaseUrl}/docs/troubleshooting/contacting-support`
                )
              }
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <Lifebuoy size={12} weight="fill" />
              Contact support
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <SupportTicketDialog
        open={ticketType !== null}
        onClose={() => setTicketType(null)}
        ticketType={ticketType ?? "bug_report"}
      />
    </>
  );
}
